import { useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Dispatch, SetStateAction } from 'react';
import type {
  ParsedJsonUpload,
  PipelineRunMode,
  PipelineStepId,
  PipelineStepState,
} from '../types';
import { PIPELINE_STEP_IDS } from '../types';
import { PipelineDebugService } from '~/app/services/pipeline-debug/pipeline-debug.service';
import {
  buildStepInput,
  summarizeStepOutput,
  validateStepInput,
} from '../utils';
import { createStepStreamHandlers } from './pipeline-step-stream.handlers';

type UsePipelineStepRunnerOptions = {
  organizationId: string;
  assetId: string;
  uploads: ParsedJsonUpload[];
  setSteps: Dispatch<SetStateAction<Record<PipelineStepId, PipelineStepState>>>;
  activeStepIndex: number;
  setActiveStepIndex: Dispatch<SetStateAction<number>>;
  runMode: PipelineRunMode;
};

export function usePipelineStepRunner({
  organizationId,
  assetId,
  uploads,
  setSteps,
  activeStepIndex,
  setActiveStepIndex,
  runMode,
}: UsePipelineStepRunnerOptions) {
  const { t } = useTranslation('pipelineDebug');

  const runStep = useCallback(
    async (stepId: PipelineStepId): Promise<boolean> => {
      if (!organizationId) {
        message.error(t('messages.organizationRequired'));
        return false;
      }

      if (!assetId) {
        message.error(t('messages.assetRequired'));
        return false;
      }

      const stepIndex = PIPELINE_STEP_IDS.indexOf(stepId);
      const previousStepId =
        stepIndex > 0 ? PIPELINE_STEP_IDS[stepIndex - 1] : undefined;

      let resolvedInput: unknown;

      setSteps((current) => {
        const previousOutput = previousStepId
          ? current[previousStepId].output
          : null;
        resolvedInput =
          current[stepId].input ??
          buildStepInput(
            stepId,
            previousOutput,
            uploads,
            stepIndex > 1 ? current.lin.output : undefined,
          );

        return {
          ...current,
          [stepId]: {
            ...current[stepId],
            status: 'running',
            input: resolvedInput,
            error: null,
            streamTotal: 0,
            streamProgress: null,
            streamItems: [],
            output: null,
          },
        };
      });

      const validation = validateStepInput(stepId, resolvedInput!);
      if (!validation.success) {
        setSteps((current) => ({
          ...current,
          [stepId]: {
            ...current[stepId],
            status: 'error',
            input: resolvedInput!,
            output: null,
            error: validation.error,
          },
        }));
        return false;
      }

      try {
        let streamError: string | null = null;
        const streamHandlers = createStepStreamHandlers(stepId, setSteps);

        const output = await PipelineDebugService.runStepStream(
          stepId,
          organizationId,
          assetId,
          resolvedInput!,
          {
            ...streamHandlers,
            onError: (errorMessage) => {
              streamError = errorMessage;
            },
          },
        );

        if (streamError) {
          throw new Error(streamError);
        }

        setSteps((current) => ({
          ...current,
          [stepId]: {
            ...current[stepId],
            status: 'completed',
            input: resolvedInput!,
            output: output ?? current[stepId].output,
            error: null,
            streamProgress: null,
          },
        }));

        message.success(
          t('messages.stepCompleted', {
            step: t(`steps.${stepId}.title`),
            summary: summarizeStepOutput(stepId, output),
          }),
        );

        return true;
      } catch (error) {
        setSteps((current) => ({
          ...current,
          [stepId]: {
            ...current[stepId],
            status: 'error',
            input: resolvedInput!,
            error:
              error instanceof Error
                ? error.message
                : t('messages.unknownError'),
            streamProgress: null,
          },
        }));
        return false;
      }
    },
    [organizationId, assetId, setSteps, t, uploads],
  );

  const runFromActiveStep = useCallback(async () => {
    const startIndex = activeStepIndex;

    for (let index = startIndex; index < PIPELINE_STEP_IDS.length; index += 1) {
      const stepId = PIPELINE_STEP_IDS[index];
      setActiveStepIndex(index);
      const succeeded = await runStep(stepId);
      if (!succeeded) {
        break;
      }

      if (runMode === 'step-by-step') {
        if (index < PIPELINE_STEP_IDS.length - 1) {
          setActiveStepIndex(index + 1);
        }
        break;
      }
    }
  }, [activeStepIndex, runMode, runStep, setActiveStepIndex]);

  const continuePipeline = useCallback(async () => {
    if (activeStepIndex >= PIPELINE_STEP_IDS.length - 1) {
      return;
    }

    const nextIndex = activeStepIndex + 1;
    setActiveStepIndex(nextIndex);
    await runStep(PIPELINE_STEP_IDS[nextIndex]);
  }, [activeStepIndex, runStep, setActiveStepIndex]);

  return {
    runStep,
    runFromActiveStep,
    continuePipeline,
  };
}
